// @flow

import React from 'react';
import {CSSTransition, TransitionGroup} from 'react-transition-group';
import styled from 'styled-components';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {UserAvatar} from 'app/components/general/avatars';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';
import type {contentType} from 'app/utils/content-type';
import type {
  AdditionalPresenceDataType,
  ConsumableSubscribedUserData,
} from '../types';
import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import {type OverlayPlacementType} from 'app/general/overlays';

type ContextType = 'view' | 'edit' | 'task';

const contextIcons = {
  view: 'far fa-eye',
  edit: 'fas fa-pen',
  task: 'fa fa-tasks',
};

function getContextTooltipText(
  context: ContextType,
  contentType: contentType,
  additionalData?: AdditionalPresenceDataType
): string | null {
  const ct = getContentType(contentType).name;

  if (context === 'view') {
    return gt('Currently viewing this %s:', ct);
  } else if (context === 'edit') {
    return gt('Currently editing this %s:', ct);
  } else if (context === 'task') {
    if (additionalData == null || additionalData.description == null) {
      return gt('Currently viewing task based on this %s', ct);
    } else {
      return gt(
        'Currently viewing %s task based on this %s',
        <b>{additionalData.description}</b>,
        ct
      );
    }
  }

  return null;
}

function tooltipText(
  context: ContextType | void,
  contentType: contentType,
  users: Array<ConsumableSubscribedUserData>
) {
  return context != null ? (
    <div className="text-left small">
      <div className="mb-2">
        {getContextTooltipText(
          context,
          contentType,
          users.length ? users[0].additionalData : undefined
        )}
      </div>
      <ul className="pl-3 m-0">
        {users.map(({user}) => (
          <li key={user.id}>{user.name || user.email}</li>
        ))}
      </ul>
    </div>
  ) : null;
}

const StyledContextDiv = styled.div`
  background: #fff;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
  border-radius: 20px;

  > .context-icon {
    position: relative;
    z-index: 1;
    font-size: 70%;
    height: 1.25rem;
    width: 1.25rem;
    padding-right: 1px;
  }

  > .avatar-border {
    transition: all 250ms;
    position: relative;
    border-radius: 20px;
    background: #fff;
    padding: 2px;
    margin-left: -2px;

    &:after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
      z-index: -1;
      border-radius: 20px;
    }

    > div {
      width: 1.6rem;
      min-width: 1.6rem;
      height: 1.6rem;
    }

    &.cb-anim-enter,
    &.cb-anim-appear,
    &.cb-anim-exit.cb-anim-exit-active {
      opacity: 0.01;
      max-width: 0;
    }

    &.cb-anim-exit,
    &.cb-anim-enter.cb-anim-enter-active,
    &.cb-anim-appear.cb-anim-appear-active {
      opacity: 1;
      max-width: 2rem;
    }

    &.cb-anim-exit-active,
    &.cb-anim-enter-active,
    &.cb-anim-appear-active {
      transition: all 250ms ease-out;
      > * {
        transition: all 250ms;
      }
    }
  }
`;

type Props = {
  contentType: contentType,
  contextName: ContextType,
  users: Array<ConsumableSubscribedUserData>,
  tooltipPlacement?: OverlayPlacementType,
};

export const PresenceContextBox = ({
  contextName,
  contentType,
  users,
  tooltipPlacement,
}: Props) => {
  return (
    <LazyTooltip
      placement={tooltipPlacement}
      hide={users.length === 0}
      tooltip={tooltipText(contextName, contentType, users)}
    >
      <StyledContextDiv className="d-flex align-items-center">
        <div className="context-icon d-flex justify-content-center align-items-center mx-1">
          <i className={`fa-fw ${contextIcons[contextName]}`} />
        </div>

        <TransitionGroup
          appear={true}
          enter={true}
          exit={true}
          component={null}
        >
          {users.map(({user}) => (
            <CSSTransition key={user.id} timeout={250} classNames="cb-anim">
              <div className="avatar-border">
                <UserAvatar
                  small={true}
                  tooltipTitle={null}
                  user={user}
                  showCard
                />
              </div>
            </CSSTransition>
          ))}
        </TransitionGroup>
      </StyledContextDiv>
    </LazyTooltip>
  );
};

export const ClickablePresenceContextBox = ({
  contextName,
  contentType,
  users,
  tooltipPlacement,
}: Props) => {
  const additionalData = users[0].additionalData;

  if (additionalData != null) {
    return (
      <FullHTMLLink html_url={additionalData.html_url}>
        <PresenceContextBox
          className="d-flex"
          contextName={contextName}
          contentType={contentType}
          users={users}
          tooltipPlacement={tooltipPlacement}
        />
      </FullHTMLLink>
    );
  } else {
    return (
      <PresenceContextBox
        className="d-flex"
        contextName={contextName}
        contentType={contentType}
        users={users}
        tooltipPlacement={tooltipPlacement}
      />
    );
  }
};
